import { dataContextGuard } from "./dataContext";
import { supabase } from "./supabase";
import { isDemoMode, isLegacyAuthMode } from "./env";
import { demoFetchJson } from "../demo/demoApi";

export const TENANT_ACCESS_CHANGED_EVENT = "truucost:tenant-access-changed";
let notifiedTenantAccessCode: string | null = null;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, message: string, code: string | null = null) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

type ApiErrorBody = {
  detail?: unknown;
  message?: unknown;
};

function apiErrorMessageFromBody(body: ApiErrorBody): string | null {
  const detailMessage = apiErrorMessageFromDetail(body.detail);
  if (detailMessage) return detailMessage;
  return apiErrorMessageFromDetail(body.message);
}

function apiErrorCodeFromBody(body: ApiErrorBody): string | null {
  for (const candidate of [body.detail, body.message]) {
    if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
      continue;
    }
    const code = (candidate as Record<string, unknown>).code;
    if (typeof code === "string" && code.trim()) return code;
  }
  return null;
}

function apiErrorMessageFromDetail(detail: unknown): string | null {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => apiErrorMessageFromValidationItem(item))
      .filter((message): message is string => Boolean(message));
    return messages.length > 0 ? messages.join("; ") : null;
  }
  if (detail && typeof detail === "object") {
    const record = detail as Record<string, unknown>;
    if (typeof record.message === "string") return record.message;
    if (typeof record.msg === "string") return record.msg;
    try {
      return JSON.stringify(detail);
    } catch {
      return null;
    }
  }
  return null;
}

function apiErrorMessageFromValidationItem(item: unknown): string | null {
  if (!item || typeof item !== "object") return null;
  const record = item as Record<string, unknown>;
  const message = typeof record.msg === "string" ? record.msg : null;
  if (!message) return null;
  const loc = Array.isArray(record.loc)
    ? record.loc.map((part) => String(part)).join(".")
    : null;
  return loc ? `${loc}: ${message}` : message;
}

export async function fetchJson<T>(
  input: RequestInfo,
  init?: RequestInit,
): Promise<T> {
  const url = typeof input === "string" ? input : input.url;
  const checkContext = url.includes("/api/auth/") ? () => {} : dataContextGuard();
  if (isDemoMode()) {
    const result = await demoFetchJson<T>(input, init); checkContext(); return result;
  }
  const response = await authorizedFetch(input, init, "application/json", checkContext);
  const result = (await response.json()) as T;
  checkContext();
  return result;
}

/**
 * Authenticated file download (e.g. CSV) through the same auth, context and
 * error contract as fetchJson. A plain link would miss the bearer token (#632).
 * Not available in the static demo; callers build demo files locally.
 */
export async function fetchBlob(input: RequestInfo, accept: string): Promise<Blob> {
  if (isDemoMode()) throw new Error("Downloads vom Server gibt es in der Demo nicht.");
  const checkContext = dataContextGuard();
  const response = await authorizedFetch(input, undefined, accept, checkContext);
  const blob = await response.blob();
  checkContext();
  return blob;
}

async function authorizedFetch(
  input: RequestInfo,
  init: RequestInit | undefined,
  accept: string,
  checkContext: () => void,
): Promise<Response> {
  let accessToken: string | undefined;
  if (!isLegacyAuthMode()) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    accessToken = session?.access_token;
  }

  const headers: Record<string, string> = {
    Accept: accept,
    ...(init?.body ? { "Content-Type": "application/json" } : {}),
    ...(init?.headers as Record<string, string> ?? {}),
  };

  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  checkContext();
  const response = await fetch(input, {
    credentials: "same-origin",
    headers,
    ...init,
  });
  checkContext();
  if (!response.ok) {
    let detail = response.statusText;
    let code: string | null = null;
    try {
      const body = (await response.json()) as ApiErrorBody;
      detail = apiErrorMessageFromBody(body) ?? detail;
      code = apiErrorCodeFromBody(body);
    } catch {
      // response was not JSON; keep the status text
    }
    checkContext();
    const error = new ApiError(response.status, detail, code);
    if (
      error.status === 423 &&
      (error.code === "tenant_paused" || error.code === "tenant_archived") &&
      !isAuthProfileRequest(input) &&
      typeof window !== "undefined" &&
      notifiedTenantAccessCode !== error.code
    ) {
      notifiedTenantAccessCode = error.code;
      window.dispatchEvent(new Event(TENANT_ACCESS_CHANGED_EVENT));
    }
    throw error;
  }
  if (isAuthProfileRequest(input)) {
    notifiedTenantAccessCode = null;
  }
  return response;
}

function isAuthProfileRequest(input: RequestInfo): boolean {
  const url = typeof input === "string" ? input : input.url;
  return url.includes("/api/auth/me") || url.includes("/api/auth/profile");
}
